import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '../hooks.js';

/**
 * Sign-in.
 *
 * The only way in is Yahoo OAuth: the Yahoo GUID is one of the few values Yahoo's
 * terms permit storing indefinitely, which makes it a natural identity anchor and
 * means this application stores no password.
 *
 * The three guarantees below are stated up front rather than buried in a privacy
 * page. Someone about to hand over account access should be able to see what the
 * portal can and cannot do before they click.
 */
export function SignInPage(): JSX.Element {
  const [params] = useSearchParams();
  const yahooError = params.get('yahooError');

  /**
   * Whether the deployment has its Yahoo credentials yet.
   *
   * Undefined while the session request is in flight, which is why the check below is
   * an explicit `=== false` — a brief flash of "not open yet" on every page load would
   * be worse than a button that is momentarily enabled.
   */
  const session = useSession();
  const notOpenYet = session.data?.yahooConfigured === false;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 1, sm: 5 } }}>
      <Stack spacing={3}>
        <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center' }}>
          {/*
            The heading is the emblem. It already sets the league name in better
            type than this page could, so a line of text repeating it underneath
            would just be the name twice. The alt text keeps it a real heading for
            anyone who cannot see it.
          */}
          <Typography
            variant="h1"
            id="page-title"
            tabIndex={-1}
            sx={{ outline: 'none', width: '100%', m: 0 }}
          >
            <Box
              component="img"
              src="/logo-wide-800.webp"
              srcSet="/logo-wide-800.webp 800w, /logo-wide-1200.webp 1200w"
              sizes="(max-width: 480px) 100vw, 440px"
              alt="La Liga de Lopie"
              sx={{ width: '100%', maxWidth: 440, display: 'block', mx: 'auto', borderRadius: 4 }}
            />
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '46ch' }}>
            Dues, prizes, weekly challenges, and the draft-order workflow, all in one place.
          </Typography>
        </Stack>

        {yahooError && <YahooErrorAlert code={yahooError} />}

        <Card variant="filled">
          <CardContent>
            <Stack spacing={2.5}>
              {notOpenYet ? (
                /*
                  Sign-in is not wired up yet. The button is withheld rather than
                  offered, because pressing it would land on somebody else's error
                  page with no way to tell whose fault it was.
                */
                <Stack spacing={1.5}>
                  <Alert severity="info">
                    <AlertTitle>Not open just yet</AlertTitle>
                    Signing in is almost ready. Your commissioner will let you know the moment it
                    opens.
                  </Alert>
                  <SetupSignIn />
                </Stack>
              ) : (
                <Button variant="contained" size="large" href="/auth/yahoo/start" fullWidth>
                  Sign in
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

/**
 * The commissioner's temporary way in, and the counterpart to `/auth/break-glass`.
 *
 * Deliberately small and folded away: the league should see the "not open yet" notice
 * and nothing that looks like a second login. It only renders while Yahoo is
 * unconfigured, and the endpoint behind it refuses once Yahoo works — so this
 * disappears on its own rather than needing to be remembered.
 */
function SetupSignIn(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const response = await fetch('/auth/break-glass', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        setError('That token was not accepted.');
        return;
      }

      // A full reload rather than a router navigation: the session cookie is new, and
      // every cached query in memory was fetched as a signed-out user.
      window.location.assign('/');
    } catch {
      setError('Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Link component="button" type="button" variant="body2" onClick={() => setOpen(true)}>
          Commissioner setup
        </Link>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={1.5}>
        <TextField
          label="Setup token"
          type="password"
          size="small"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          autoFocus
          fullWidth
          error={Boolean(error)}
          helperText={error ?? 'Temporary, and only until sign-in opens.'}
        />
        <Button type="submit" variant="outlined" disabled={busy || token.length === 0}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </Stack>
    </Box>
  );
}

function YahooErrorAlert({ code }: { code: string }): JSX.Element {
  const { severity, message } = describeOAuthError(code);
  return (
    <Alert severity={severity}>
      <Typography variant="body2">{message}</Typography>
    </Alert>
  );
}

/**
 * Explains a failed OAuth attempt.
 *
 * Each case gets its own wording because the recovery differs: a declined consent
 * screen is not the same problem as an expired state or a rejected credential, and
 * one generic message would leave a user guessing which.
 */
export function describeOAuthError(code: string): {
  severity: 'error' | 'warning' | 'info';
  message: string;
} {
  switch (code) {
    case 'oauth_denied':
      return {
        severity: 'info',
        message:
          'You declined access on Yahoo’s screen, so nothing was connected. Sign in again if that was not intended.',
      };

    case 'yahoo_scope_denied':
      /*
        A configuration fault, not the reader's mistake. Saying so plainly stops
        twelve people retrying a button that cannot work, and tells the one person
        who can fix it what to fix.
      */
      return {
        severity: 'error',
        message:
          'Sign-in is not finished being set up — the connection is missing a permission it needs. Nothing you did caused this; your commissioner has been told.',
      };

    case 'oauth_state_expired':
      return {
        severity: 'warning',
        message: 'That sign-in attempt timed out before it finished. Start again.',
      };

    case 'oauth_state_reused':
      return {
        severity: 'warning',
        message: 'That sign-in link had already been used. Start a fresh sign-in.',
      };

    case 'oauth_state_invalid':
    case 'oauth_state_missing':
      return {
        severity: 'warning',
        message:
          'That sign-in attempt could not be verified, so it was rejected. Start again from this page.',
      };

    case 'oauth_exchange_failed':
      return {
        severity: 'error',
        message:
          'Yahoo accepted the sign-in but the portal could not complete it. Try again — if it keeps ' +
          'failing, the Yahoo application credentials may need attention.',
      };

    case 'yahoo_needs_reconnect':
      return {
        severity: 'warning',
        message: 'Your previous Yahoo authorization is no longer valid. Sign in again to renew it.',
      };

    case 'yahoo_unavailable':
      return { severity: 'warning', message: 'Yahoo did not respond. Try again shortly.' };

    case 'yahoo_rate_limited':
      return {
        severity: 'warning',
        message: 'Yahoo is rate limiting requests right now. Wait a moment and try again.',
      };

    default:
      return { severity: 'error', message: 'The Yahoo sign-in did not complete. Try again.' };
  }
}
